import React, { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import { LanguageProvider } from "./lang/LanguageContext";
import Footer from "@/components/Footer";
import SeedToBloomIntro from "@/components/SeedToBloomIntro";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Program from "@/pages/Program";
import Safety from "@/pages/Safety";
import Enroll from "@/pages/Enroll";
import Forms from "@/pages/Forms";
import Parents from "@/pages/Parents";
import Contact from "@/pages/Contact";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error) {
        console.error("Render error:", error);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-cream text-soil p-8 text-center">
                    <div>
                        <p className="font-display text-3xl mb-3">Oh, sprouts.</p>
                        <p className="mb-5 text-soilmuted">Something wilted on this page. A quick refresh should bring the garden back.</p>
                        <Link to="/" className="inline-block bg-sun text-soil font-bold px-6 py-3 rounded-full hover-lift" data-testid="error-back-home-link">
                            Back to the home page
                        </Link>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const SmoothScroll = () => {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    return null;
};

const NotFound = () => (
    <section className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5 pt-24 pb-16">
        <p className="text-7xl mb-4" aria-hidden="true">
            <svg viewBox="0 0 64 64" className="w-20 h-20 mx-auto">
                <rect x="2" y="2" width="60" height="60" rx="22" fill="#A8D5E2" />
                <path d="M32 50V30" stroke="#3D3229" strokeWidth="3.5" strokeLinecap="round" />
                <path d="M32 38c-7 1-10-2-11-7 6-1 10 2 11 7z" fill="#FFF8EC" />
                <circle cx="32" cy="24" r="7" fill="#F6C544" />
            </svg>
        </p>
        <h1 className="font-display text-3xl md:text-4xl mb-3">This page hasn't sprouted yet</h1>
        <p className="text-soilmuted mb-6">The link may be old — let's get you back to the garden.</p>
        <Link to="/" className="bg-sun text-soil font-bold px-6 py-3 rounded-full hover-lift" data-testid="not-found-home-button">
            Back to Home
        </Link>
    </section>
);

function App() {
    return (
        <ErrorBoundary>
            <LanguageProvider>
                <BrowserRouter>
                <SmoothScroll />
                <ScrollToTop />
                <SeedToBloomIntro />
                <Toaster position="top-center" richColors />
                <div className="grain relative min-h-screen bg-cream text-soil font-body overflow-x-clip">
                    <Header />
                    <main>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/program" element={<Program />} />
                            <Route path="/safety" element={<Safety />} />
                            <Route path="/enroll" element={<Enroll />} />
                            <Route path="/forms" element={<Forms />} />
                            <Route path="/parents" element={<Parents />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </main>
                    <Footer />
                </div>
            </BrowserRouter>
            </LanguageProvider>
        </ErrorBoundary>
    );
}

export default App;
